import { useState } from "react";

import {
  useGlobalComponents,
  useLinkComponent,
} from "../../hooks/useComponents";
import type { ComponentRecord, ComponentType } from "../../types/components";
import { VerifiedPendingBadge } from "./VerifiedPendingBadge";

interface Props {
  projectId: string;
  componentType: ComponentType;
  onClose: () => void;
}

export function BrowseCatalogModal({
  projectId,
  componentType,
  onClose,
}: Props) {
  const [q, setQ] = useState("");
  const { data, isLoading } = useGlobalComponents({
    type: componentType,
    q: q || undefined,
    linked: false,
    limit: 200,
  });
  const link = useLinkComponent(projectId);

  return (
    <div
      role="dialog"
      aria-modal="true"
      data-testid="browse-catalog-modal"
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.4)",
        display: "grid",
        placeItems: "center",
        zIndex: 50,
      }}
    >
      <div
        style={{
          width: "min(720px, 95vw)",
          maxHeight: "85vh",
          background: "white",
          borderRadius: 8,
          padding: "1.5rem",
          overflow: "auto",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <h2 style={{ margin: 0 }}>Browse {componentType}s</h2>
          <button
            type="button"
            data-testid="browse-catalog-close"
            onClick={onClose}
            style={closeBtnStyle}
          >
            ×
          </button>
        </div>
        <input
          type="search"
          placeholder="Filter by manufacturer or model number"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          data-testid="browse-catalog-search"
          style={{
            width: "100%",
            margin: "1rem 0",
            padding: "0.5rem",
            border: "1px solid #d1d5db",
            borderRadius: 6,
          }}
        />
        {isLoading && <div role="status">Loading catalog…</div>}
        {data && data.components.length === 0 && (
          <p data-testid="browse-catalog-empty">
            No matching components in the global catalog.
          </p>
        )}
        <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
          {data?.components.map((c: ComponentRecord) => (
            <li
              key={c.id}
              data-testid={`browse-catalog-row-${c.id}`}
              style={{
                padding: "0.5rem 0",
                borderBottom: "1px solid #f3f4f6",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <div>
                <div style={{ fontWeight: 600 }}>{c.manufacturer}</div>
                <div style={{ fontSize: "0.85rem", color: "#374151" }}>
                  {c.model_number}
                </div>
                <div style={{ marginTop: 4 }}>
                  <VerifiedPendingBadge component={c} />
                </div>
              </div>
              <button
                type="button"
                data-testid={`browse-catalog-link-${c.id}`}
                disabled={link.isPending}
                onClick={() =>
                  link.mutate(c.id, {
                    onSuccess: () => {
                      // Modal stays open; user keeps browsing.
                    },
                  })
                }
                style={linkBtnStyle}
              >
                Link
              </button>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

const closeBtnStyle: React.CSSProperties = {
  background: "transparent",
  border: "none",
  fontSize: "1.5rem",
  cursor: "pointer",
};

const linkBtnStyle: React.CSSProperties = {
  background: "#2563eb",
  color: "white",
  padding: "0.4rem 0.75rem",
  border: "none",
  borderRadius: 6,
  cursor: "pointer",
};
